// Incoming call (docs/06 §2): the take-over that rings for an incoming invite.
// It wires the engine, ringtone, navigation, and cold-open provisional pickup
// around the presentation-only RingSurface (Android-parity look). Two mounts:
//   - variant 'route' (default): the standalone /incoming full-screen page used
//     on compact and as the deep-link target — navigation-driven.
//   - variant 'modal': the desktop AppShell overlay — no route changes; the
//     AppShell gates the mount off engine/pickup state.
//
// Accept is the user gesture that unlocks the audio session on iOS Safari
// (docs/06 §3) — `media.unlockAudio()` runs inside the tap, before the accept.
//
// Fast cold-open: a notification click deep-links `#invite=<callId>` and the
// PendingInvitePickup publishes it before the engine has reconnected and
// re-received the invite. Rather than sit on a loading screen, we ring
// immediately in a "provisional" state (generic caller, spinner on accept). The
// real accept fires the moment the matching invite actually rings; if the engine
// instead reports that invite dead/stale, we fall through to Main with no ring
// artifacts.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { TIMEOUTS, type Friend } from '@merkury/protocol';
import { candidateSeed } from '../../avatar/mercury.js';
import { Store } from '@merkury/brain/call/store.js';
import { isCapacitorNative } from '../../shell/capacitor/index.js';
import { useT } from '../../i18n/index.js';
import type { PendingInvite, PendingInvitePickup } from '../../push/pendingInvite.js';
import { useScreens } from '../ScreensContext.js';
import { useStore } from '../ui/useStore.js';
import { displayNameFor } from '../ui/peerName.js';
import { initialOf } from '../ui/PeerAvatar.js';
import { type TonePlayer } from '@merkury/brain/call/reconnectTone.js';
import { createRingtoneFor } from '@merkury/brain/call/ringtones.js';
import { getRingtoneId } from '../../storage/ringtonePref.js';
import { RingSurface } from './RingSurface.js';

export type IncomingVariant = 'route' | 'modal';

export interface IncomingCallScreenProps {
  ringtone?: TonePlayer;
  /** Cold-open provisional source; when omitted there is no provisional state. */
  pickup?: PendingInvitePickup;
  variant?: IncomingVariant;
}

// Stable empty store so the pickup subscription is unconditional even when no
// pickup is injected (a route mount without cold-open plumbing, or a test).
const NO_PENDING = new Store<PendingInvite | null>(null);

export function IncomingCallScreen({
  ringtone,
  pickup: pickupProp,
  variant = 'route',
}: IncomingCallScreenProps): ReactNode {
  const t = useT();
  const { engine, media, api, pickup: pickupCtx } = useScreens();
  // Prop wins (tests/modal mounts); the route mount gets it from ScreensDeps.
  const pickup = pickupProp ?? pickupCtx;
  const navigate = useNavigate();
  const engineState = useStore(engine.state);
  const { call, lastEnded } = engineState;
  const pending = useStore(pickup?.state ?? NO_PENDING);

  const [friends, setFriends] = useState<Friend[]>([]);
  useEffect(() => {
    let cancelled = false;
    void api
      .listFriends()
      .then((res) => {
        if (!cancelled) setFriends(res.friends);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api]);

  const liveRinging = call?.direction === 'incoming' && call.phase === 'ringing';

  // A pending invite whose call hasn't reached the engine yet drives the
  // provisional ring — unless it can no longer be answered: past the server's
  // 45 s ring window, or once the engine confirms that exact call already ended
  // (ghost invite). Either way there is nothing to answer, so no ring for it.
  const staleInvite =
    pending !== null &&
    (Date.now() - pending.receivedAt > TIMEOUTS.RING_MS ||
      (lastEnded !== null && lastEnded.callId === pending.callId));
  // A queued 'decline' decision needs no surface: installAutoAccept completes
  // it silently when the invite lands — ringing for it would re-ask.
  const provisional =
    !liveRinging && pending !== null && pending.action !== 'decline' && !staleInvite;
  const showRing = liveRinging || provisional;

  // Accept queued from the provisional surface (bare cold-open click): the
  // decision waits for the real invite to ring, then accepts the MATCHING call
  // so a stale queue can never answer a later, different one. A native-accept
  // deep link (pending.action==='accept') is completed by the global
  // installAutoAccept seam instead — here we only reflect it as the spinner.
  const [tappedAccept, setTappedAccept] = useState(false);
  const accepting = tappedAccept || pending?.action === 'accept';
  useEffect(() => {
    if (!tappedAccept) return;
    if (
      call !== null &&
      call.phase === 'ringing' &&
      call.direction === 'incoming' &&
      (pending === null || call.callId === pending.callId)
    ) {
      engine.accept();
      setTappedAccept(false);
    }
  }, [tappedAccept, call, pending, engine]);

  // Audible ring on the WEB app only: Android already plays the system ringtone
  // natively (CallNotificationHelper) and must not double-ring. On a gestureless
  // cold open the AudioContext may still be locked (Safari) — createRingtone
  // swallows that silently, so the audible ring simply starts once unlocked
  // while the visual ring never waits on it.
  const ringtoneRef = useRef<TonePlayer | null>(null);
  ringtoneRef.current ??= ringtone ?? createRingtoneFor(getRingtoneId());
  useEffect(() => {
    const player = ringtoneRef.current!;
    if (showRing && !isCapacitorNative()) player.start();
    else player.stop();
    return () => player.stop();
  }, [showRing]);

  // Route mode leaves /incoming when there is nothing to ring: to the in-call
  // screen if the call advanced (accepted → connecting), else home (missed /
  // declined / stale). Modal mode never navigates — the AppShell owns the mount.
  useEffect(() => {
    if (variant !== 'route') return;
    if (showRing) return;
    if (call && call.phase !== 'ringing') void navigate('/call', { replace: true });
    else void navigate('/', { replace: true });
  }, [variant, showRing, call, navigate]);

  // A stale pending invite must be dropped so it can't re-provision on remount.
  useEffect(() => {
    if (staleInvite) pickup?.clear();
  }, [staleInvite, pickup]);

  if (!showRing) return null;

  const peerName = liveRinging
    ? displayNameFor(call.peerUserId, friends)
    : t('incoming.incomingCall');
  const friend = liveRinging ? friends.find((f) => f.userId === call.peerUserId) : undefined;
  const avatarSeed =
    liveRinging && friend?.avatarSeed !== undefined
      ? candidateSeed(call.peerUserId, friend.avatarSeed)
      : undefined;
  const caption = liveRinging ? t('incoming.isCalling') : t('incoming.connecting');

  const onAccept = (): void => {
    // iOS gesture rule: unlock audio inside the tap, before any accept.
    void media.unlockAudio();
    if (liveRinging) {
      engine.accept();
      if (variant === 'route') void navigate('/call', { replace: true });
    } else {
      // Provisional: queue — the invite is still arriving over the WSS.
      setTappedAccept(true);
    }
  };

  const onDecline = (): void => {
    if (liveRinging) engine.decline();
    // Provisional: queue the decline (JW7 seam) — installAutoAccept completes
    // it against the matching invite when it lands, so the caller's ring ends
    // instead of the call re-ringing here after a local-only dismissal.
    else if (pending !== null) pickup?.decide(pending.callId, 'decline');
    setTappedAccept(false);
    if (variant === 'route') void navigate('/', { replace: true });
  };

  return (
    <RingSurface
      title={peerName}
      caption={caption}
      {...(avatarSeed ? { avatarSeed } : {})}
      {...(liveRinging && !avatarSeed ? { initial: initialOf(peerName) } : {})}
      accepting={provisional && accepting}
      onAccept={onAccept}
      onDecline={onDecline}
      variant={variant === 'modal' ? 'modal' : 'fullscreen'}
    />
  );
}
